import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileAudio, FileVideo, Image as ImageIcon, Trash2, Upload } from "lucide-react";
import { adminGet, adminDel, authHeaders } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function IconFor({ type }) {
  if (type?.startsWith("video")) return <FileVideo size={20} strokeWidth={1.5} />;
  if (type?.startsWith("image")) return <ImageIcon size={20} strokeWidth={1.5} />;
  return <FileAudio size={20} strokeWidth={1.5} />;
}

export default function MediaLibrary() {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => adminGet("/media").then((d) => setMedia(d.media)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/admin/media`, form, { headers: authHeaders() });
      toast("Fichier ajouté à la médiathèque.");
      load();
    } catch (err) {
      toast(err.response?.data?.detail || "Le dépôt a échoué. Réessayez.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (m) => {
    await adminDel(`/media/${m.id}`).catch(() => {});
    toast("Fichier retiré.");
    load();
  };

  return (
    <div data-testid="admin-media-module">
      <h2 className="rav-admin-h2">Médiathèque</h2>
      <p className="rav-page-sub">
        Déposez vos audios et vidéos de séance ici, puis rattachez-les à un contenu (module Contenus). Ils seront lus
        directement dans l'application.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*,video/*,image/*"
        style={{ display: "none" }}
        data-testid="media-file-input"
        onChange={upload}
      />
      <button
        type="button"
        className="rav-primary-button rav-admin-create"
        data-testid="media-upload-button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        <Upload size={16} strokeWidth={1.6} />
        {uploading ? "Dépôt en cours…" : "Déposer un fichier"}
      </button>

      <div className="rav-admin-list">
        {media.length === 0 && <p className="rav-page-sub">Vos audios et vidéos apparaîtront ici.</p>}
        {media.map((m, i) => (
          <div key={m.id} className="rav-admin-row" data-testid={`media-row-${i}`}>
            <span className="rav-admin-row-label rav-media-label">
              <IconFor type={m.content_type} />
              {m.original_filename}
            </span>
            <span className="rav-admin-row-sub">
              {m.content_type} · {(m.size / (1024 * 1024)).toFixed(1)} Mo · {String(m.created_at).slice(0, 10)}
            </span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`media-delete-${i}`} onClick={() => remove(m)}>
                <Trash2 size={13} strokeWidth={1.6} /> Retirer
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
